import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, FileText } from "lucide-react";

import AnimatedHeading from "../../animations/AnimatedHeading";
import LightRays from "../../animations/LightRays";
import Noise from "../../animations/Noise";
import TiltedCard from "../../animations/TiltedCard";
import SpotlightCard from "../../animations/SpotlightCard";

import PolicyCard from "./PolicyCard";
import privacyData from "./privacyData";

const stats = [
  {
    number: "100%",
    title: "Data Protection",
    icon: ShieldCheck,
  },
  {
    number: "24/7",
    title: "Security Monitoring",
    icon: Lock,
  },
  {
    number: "8+",
    title: "Privacy Standards",
    icon: FileText,
  },
];

const PrivacyPolicy = () => {
  return (
    <div
      className="relative overflow-hidden font-saira"
      style={{
        backgroundColor: "var(--bg-primary)",
        color: "var(--text-primary)",
      }}
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <LightRays />
        </div>

        <Noise
          patternSize={180}
          patternScaleX={1}
          patternScaleY={1}
          patternRefreshInterval={4}
          patternAlpha={12}
        />

        <div
          className="absolute -left-28 -top-28 h-72 w-72 rounded-full blur-[120px]"
          style={{
            backgroundColor: "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
          }}
        />

        <div
          className="absolute -right-24 bottom-0 h-80 w-80 rounded-full blur-[130px]"
          style={{
            backgroundColor: "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
          }}
        />

        <div
          className="absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full blur-[120px]"
          style={{
            backgroundColor: "color-mix(in srgb, var(--brand-primary) 5%, transparent)",
          }}
        />
      </div>

      <section className="relative z-10">
        <div className="mx-auto max-w-7xl px-5 pt-28 pb-16 sm:px-6 sm:pt-32 sm:pb-20 lg:pt-36">
          <motion.div
            initial={{ opacity: 0, y: 35 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center"
          >
            <motion.p
              initial={{ opacity: 0, letterSpacing: "0.6em" }}
              animate={{ opacity: 1, letterSpacing: "0.3em" }}
              transition={{ delay: 0.15 }}
              className="text-[11px] font-bold uppercase tracking-[0.3em] text-primaryColor sm:text-xs md:text-sm"
            >
              [ Privacy Policy ]
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mx-auto max-w-6xl text-3xl font-extrabold leading-tight sm:text-4xl md:text-5xl lg:text-6xl"
              style={{ color: "var(--text-primary)" }}
            >
              Protecting Your{" "}
              <span style={{ color: "var(--brand-primary)" }}>Privacy</span>
              <br />
              With Transparency
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.4,
                duration: 0.6,
              }}
              className="mx-auto mt-6 max-w-3xl text-sm leading-7 sm:text-base sm:leading-8 lg:text-lg"
              style={{ color: "var(--text-muted)" }}
            >
              At Web_Bocket, protecting your personal information is one of our
              highest priorities. This Privacy Policy explains how we collect,
              process, store and safeguard your information while delivering our
              digital products and professional software development services.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="relative z-10 pb-16">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-5 sm:grid-cols-2 sm:px-6 lg:grid-cols-3">
          {stats.map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  y: 35,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.45,
                  delay: index * 0.1,
                }}
              >
                <TiltedCard containerHeight="100%" containerWidth="100%">
                  <SpotlightCard
                    tailwindColor="#E67000"
                    alpha={0.12}
                    radius={260}
                    className="h-full rounded-3xl"
                  >
                    <motion.div
                      whileHover={{
                        y: -6,
                      }}
                      className="group h-full rounded-3xl border p-6 text-center transition-all duration-300 hover:border-orange-500/40"
                      style={{
                        backgroundColor: "var(--card-bg)",
                        borderColor: "var(--border-primary)",
                        color: "var(--text-primary)",
                      }}
                    >
                      <div
                        className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border transition-all duration-300 group-hover:rotate-6 group-hover:scale-105"
                        style={{
                          borderColor:
                            "color-mix(in srgb, var(--brand-primary) 20%, transparent)",
                          backgroundColor:
                            "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
                        }}
                      >
                        <Icon
                          size={26}
                          style={{ color: "var(--brand-primary)" }}
                        />
                      </div>

                      <motion.h2
                        whileHover={{
                          scale: 1.05,
                        }}
                        className="mt-5 text-4xl font-bold"
                        style={{ color: "var(--brand-primary)" }}
                      >
                        {item.number}
                      </motion.h2>

                      <p
                        className="mt-2"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {item.title}
                      </p>

                      <div
                        className="mx-auto mt-5 h-[2px] w-0 rounded-full transition-all duration-500 group-hover:w-20"
                        style={{ backgroundColor: "var(--brand-primary)" }}
                      />
                    </motion.div>
                  </SpotlightCard>
                </TiltedCard>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="relative z-10 pb-16 sm:pb-20 lg:pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 35 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="mb-10 text-center sm:mb-14 lg:mb-16"
          >
            <motion.p
              initial={{ opacity: 0, letterSpacing: "0.5em" }}
              whileInView={{
                opacity: 1,
                letterSpacing: "0.3em",
              }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-[11px] font-bold uppercase tracking-[0.3em] text-primaryColor sm:text-xs md:text-sm"
            >
              [ Our Commitment ]
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mx-auto mt-5 max-w-5xl text-3xl font-extrabold leading-tight sm:text-4xl md:text-5xl lg:text-5xl"
              style={{ color: "var(--text-primary)" }}
            >
              How We Protect{" "}
              <span style={{ color: "var(--brand-primary)" }}>
                Your Information
              </span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                delay: 0.2,
                duration: 0.5,
              }}
              className="mx-auto mt-5 max-w-3xl text-sm leading-7 sm:text-base sm:leading-8"
              style={{ color: "var(--text-muted)" }}
            >
              Transparency is the foundation of trust. These principles explain
              how we responsibly collect, process, secure, and manage your
              information while delivering our digital products and professional
              software development services.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            variants={{
              hidden: {},
              show: {
                transition: {
                  staggerChildren: 0.12,
                },
              },
            }}
            className="grid grid-cols-1 gap-6 md:grid-cols-2"
          >
            {privacyData.map((item, index) => (
              <motion.div
                key={index}
                variants={{
                  hidden: {
                    opacity: 0,
                    y: 35,
                  },
                  show: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.45,
                    },
                  },
                }}
              >
                <PolicyCard
                  index={index}
                  icon={item.icon}
                  title={item.title}
                  description={item.description}
                  points={item.points}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;